import { useAnalytics } from "@/components/context/analytics-context";
import { LeadCaptureModal } from "@/components/lead-capture-modal";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { SDKData } from "@/types";
import { AnimatePresence, motion } from "framer-motion";
import { X, XIcon } from "lucide-react";
import * as React from "react";
import { useRef, useState } from "react";
import FAB from "./fab";
import PlayIcon from "./icons/Play";
import VideoPlayer from "./video-player";

export interface VideoFeedWidgetProps {
  data: SDKData;
  apiKey: string;
  variant: "widget" | "story" | "lightbox";
  position?: "bottom-right" | "bottom-left";
  theme?: {
    primaryColor: string;
    textColor: string;
  };
  container?: HTMLElement;
}

export const VideoFeedWidget: React.FC<VideoFeedWidgetProps> = ({
  apiKey,
  data: { videos, formData, widgetData, logoUrl, playerData, isRtl },
  variant,
  position = "bottom-right",
  theme = {
    primaryColor: "bg-primary",
    textColor: "text-primary-foreground",
  },
  container,
}) => {
  const { trackEvent } = useAnalytics();
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const constraintsRef = useRef(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isOpened, setIsOpened] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  React.useEffect(() => {
    trackEvent("sdk_impression");
  }, []);

  const renderContent = () => (
    <Card
      className={cn(
        "fixed md:relative inset-0 w-full h-full rounded-none border-none md:border md:rounded-2xl overflow-hidden",
        {
          "max-w-3xl mx-auto": variant === "lightbox",
        }
      )}
    >
      <LeadCaptureModal
        portalContainer={container}
        isRtl={isRtl}
        formData={formData}
        apiKey={apiKey}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
      <CardHeader className="absolute top-0 left-0 right-0 bg-transparent z-10 py-4 px-2">
        <CardTitle className="flex justify-between items-center">
          <img
            src={logoUrl}
            alt={logoUrl}
            className="h-12 w-auto rounded-full"
          />
          <Button
            size="icon"
            onClick={() => setIsOpen(false)}
            className="w-10 h-10 rounded-full opacity-50 hover:opacity-100"
          >
            <X className="h-8 w-8" />
            <span className="sr-only">Close</span>
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 overflow-hidden h-full" ref={constraintsRef}>
        <VideoPlayer shouldMute={isModalOpen} videos={videos} />
      </CardContent>
      {!isModalOpen && (
        <CardFooter className="absolute bottom-0 left-1/2 -translate-x-1/2 bg-transparent z-10 w-full bg-gradient-to-t from-black/80 to-transparent flex flex-col items-center justify-center md:rounded-b-2xl pb-6">
          <Button
            onClick={() => {
              trackEvent("sdk_cta_clicked", {
                ctaType: playerData.ctaType ?? "form",
                ...(playerData.ctaType === "link"
                  ? { ctaLink: playerData.ctaLink }
                  : {}),
              });

              if (playerData.ctaType === "link") {
                window.open(
                  playerData.ctaLink ?? "https://webreels.io/",
                  "_blank"
                );
              } else {
                setIsModalOpen(true);
              }
            }}
            className="w-full bg-white text-black rounded-[50px] hover:bg-white/80 text-lg font-medium h-14 mb-2"
          >
            {playerData.ctaTitle}
          </Button>
          <button
            className="absolute bottom-0 flex flex-row justify-center items-center w-full z-20 p-2"
            onClick={(e) => {
              e.stopPropagation();
              window.open("https://webreels.io/", "_blank");
            }}
          >
            <div className="text-white z-20 font-bold text-[8px] mr-1 uppercase">
              Powered by
            </div>
            <svg
              className="text-white mb-[1px]"
              width="63"
              height="8"
              viewBox="0 0 63 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0.335796 0.591456H2.85381L3.97293 5.48759L3.57324 5.4776L4.8922 0.591456H7.68L9.05891 5.52756H8.73917L9.85828 0.591456H12.3563L10.5977 7.58594H7.40022L6.0413 2.84967H6.28111L4.99212 7.58594H2.1044L0.335796 0.591456ZM16.6502 7.78578C15.8442 7.78578 15.1447 7.65255 14.5518 7.3861C13.959 7.11298 13.5027 6.73328 13.1829 6.24699C12.8698 5.76071 12.7133 5.20448 12.7133 4.57831C12.7133 3.98544 12.8565 3.45586 13.143 2.98956C13.4361 2.5166 13.8557 2.1469 14.402 1.88044C14.9482 1.61398 15.591 1.48075 16.3304 1.48075C17.0632 1.48075 17.676 1.60066 18.169 1.84047C18.6686 2.08028 19.0416 2.42334 19.2881 2.86966C19.5346 3.30931 19.6578 3.8289 19.6578 4.42843V5.0979H13.7325V4.02874H17.9392L17.7193 4.47839V4.29853C17.7193 4.07204 17.6694 3.87886 17.5695 3.71899C17.4762 3.55911 17.333 3.43921 17.1398 3.35927C16.9533 3.27267 16.7235 3.22937 16.4503 3.22937C16.1572 3.22937 15.9074 3.27934 15.7009 3.37926C15.5011 3.47252 15.3479 3.60908 15.2413 3.78893C15.1347 3.96213 15.0814 4.16197 15.0814 4.38846L15.0315 4.4584C15.0381 4.76483 15.1247 5.03129 15.2913 5.25777C15.4645 5.4776 15.7176 5.64747 16.0507 5.76737C16.3837 5.88062 16.7934 5.93724 17.2797 5.93724C17.706 5.93724 18.0924 5.88728 18.4388 5.78736C18.7918 5.68744 19.1316 5.53755 19.458 5.33771V7.06635C19.1249 7.28617 18.7086 7.45937 18.209 7.58594C17.716 7.71917 17.1964 7.78578 16.6502 7.78578ZM25.4123 7.78578C24.986 7.78578 24.603 7.7225 24.2632 7.59593C23.9302 7.47602 23.6437 7.29284 23.4039 7.04636C23.1708 6.79323 22.9876 6.48014 22.8544 6.1071L22.9743 5.95722V7.58594H20.5362V0.391614H22.9743V3.61907L22.8943 3.47918C22.9143 3.4059 22.931 3.33263 22.9443 3.25935C23.0509 2.87965 23.2174 2.55657 23.4439 2.29012C23.6704 2.02366 23.9535 1.82382 24.2932 1.69059C24.633 1.5507 25.0293 1.48075 25.4823 1.48075C26.0352 1.48075 26.5181 1.61731 26.9311 1.89043C27.3508 2.16355 27.6706 2.53992 27.8904 3.01954C28.1169 3.4925 28.2301 4.02541 28.2301 4.61828C28.2301 5.23779 28.1102 5.78736 27.8704 6.26698C27.6306 6.7466 27.2975 7.11964 26.8712 7.3861C26.4449 7.65255 25.9586 7.78578 25.4123 7.78578ZM24.3032 5.91725C24.5763 5.91725 24.8161 5.86396 25.0226 5.75738C25.2291 5.64414 25.389 5.49092 25.5023 5.29774C25.6155 5.0979 25.6721 4.87474 25.6721 4.62827C25.6721 4.3818 25.6155 4.16197 25.5023 3.96879C25.3957 3.77561 25.2391 3.62573 25.0326 3.51915C24.8328 3.4059 24.5963 3.34928 24.3232 3.34928C24.0501 3.34928 23.8103 3.4059 23.6038 3.51915C23.3973 3.62573 23.2374 3.77894 23.1241 3.97878C23.0176 4.17863 22.9643 4.40178 22.9643 4.64826C22.9643 4.89473 23.0176 5.11455 23.1241 5.30773C23.2307 5.50092 23.3839 5.6508 23.5838 5.75738C23.7903 5.86396 24.0301 5.91725 24.3032 5.91725ZM29.1157 0.591456H34.0419C34.5881 0.591456 35.0644 0.691377 35.4707 0.89122C35.8771 1.0844 36.1902 1.35752 36.41 1.71057C36.6298 2.06363 36.7397 2.47664 36.7397 2.9496C36.7397 3.38925 36.6465 3.77894 36.4599 4.11867C36.2734 4.45174 36.0103 4.71154 35.6706 4.89806C35.3308 5.08458 34.9378 5.17784 34.4915 5.17784H33.832L34.1318 4.4584L36.8097 7.58594H33.872L31.314 4.3385V3.97878H33.0227C33.389 3.97878 33.6688 3.90884 33.862 3.76895C34.0552 3.6224 34.1518 3.41256 34.1518 3.13945C34.1518 2.87965 34.0618 2.68314 33.882 2.54991C33.7021 2.41668 33.439 2.35007 33.0926 2.35007H31.6138V7.58594H29.1157V0.591456ZM41.2987 7.78578C40.4927 7.78578 39.7932 7.65255 39.2004 7.3861C38.6075 7.11298 38.1512 6.73328 37.8314 6.24699C37.5184 5.76071 37.3618 5.20448 37.3618 4.57831C37.3618 3.98544 37.505 3.45586 37.7915 2.98956C38.0846 2.5166 38.5042 2.1469 39.0505 1.88044C39.5967 1.61398 40.2395 1.48075 40.979 1.48075C41.7117 1.48075 42.3246 1.60066 42.8175 1.84047C43.3171 2.08028 43.6902 2.42334 43.9366 2.86966C44.1831 3.30931 44.3063 3.8289 44.3063 4.42843V5.0979H38.381V4.02874H42.5877L42.3679 4.47839V4.29853C42.3679 4.07204 42.3179 3.87886 42.218 3.71899C42.1247 3.55911 41.9815 3.43921 41.7883 3.35927C41.6018 3.27267 41.372 3.22937 41.0989 3.22937C40.8058 3.22937 40.556 3.27934 40.3495 3.37926C40.1496 3.47252 39.9964 3.60908 39.8898 3.78893C39.7832 3.96213 39.7299 4.16197 39.7299 4.38846L39.68 4.4584C39.6866 4.76483 39.7732 5.03129 39.9398 5.25777C40.113 5.4776 40.3661 5.64747 40.6992 5.76737C41.0323 5.88062 41.4419 5.93724 41.9282 5.93724C42.3545 5.93724 42.7409 5.88728 43.0873 5.78736C43.4404 5.68744 43.7801 5.53755 44.1065 5.33771V7.06635C43.7734 7.28617 43.3571 7.45937 42.8575 7.58594C42.3645 7.71917 41.8449 7.78578 41.2987 7.78578ZM48.8318 7.78578C48.0258 7.78578 47.3264 7.65255 46.7335 7.3861C46.1406 7.11298 45.6843 6.73328 45.3646 6.24699C45.0515 5.76071 44.8949 5.20448 44.8949 4.57831C44.8949 3.98544 45.0382 3.45586 45.3246 2.98956C45.6177 2.5166 46.0374 2.1469 46.5836 1.88044C47.1298 1.61398 47.7727 1.48075 48.5121 1.48075C49.2448 1.48075 49.8577 1.60066 50.3506 1.84047C50.8502 2.08028 51.2233 2.42334 51.4697 2.86966C51.7162 3.30931 51.8395 3.8289 51.8395 4.42843V5.0979H45.9141V4.02874H50.1208L49.901 4.47839V4.29853C49.901 4.07204 49.851 3.87886 49.7511 3.71899C49.6578 3.55911 49.5146 3.43921 49.3214 3.35927C49.1349 3.27267 48.9051 3.22937 48.632 3.22937C48.3389 3.22937 48.0891 3.27934 47.8826 3.37926C47.6827 3.47252 47.5295 3.60908 47.4229 3.78893C47.3164 3.96213 47.2631 4.16197 47.2631 4.38846L47.2131 4.4584C47.2198 4.76483 47.3064 5.03129 47.4729 5.25777C47.6461 5.4776 47.8992 5.64747 48.2323 5.76737C48.5654 5.88062 48.9751 5.93724 49.4613 5.93724C49.8877 5.93724 50.274 5.88728 50.6204 5.78736C50.9735 5.68744 51.3132 5.53755 51.6396 5.33771V7.06635C51.3065 7.28617 50.8902 7.45937 50.3906 7.58594C49.8977 7.71917 49.3781 7.78578 48.8318 7.78578ZM52.7178 0.391614H55.1559V7.58594H52.7178V0.391614ZM59.2172 7.78578C58.6443 7.78578 58.088 7.71584 57.5485 7.57595C57.0089 7.43606 56.5226 7.23288 56.0896 6.96643V5.28775C56.2961 5.45429 56.5559 5.60417 56.869 5.7374C57.1888 5.87062 57.5252 5.97388 57.8782 6.04715C58.2313 6.12043 58.571 6.15706 58.8974 6.15706C59.0639 6.15706 59.2005 6.14374 59.3071 6.1171C59.4203 6.09045 59.5003 6.05381 59.5469 6.00718C59.6002 5.96055 59.6268 5.91059 59.6268 5.8573C59.6268 5.75738 59.5935 5.67411 59.5269 5.6075C59.4603 5.54088 59.3304 5.48426 59.1372 5.43763L58.0381 5.16785C57.3853 5.00797 56.9223 4.78814 56.6492 4.50837C56.3761 4.22192 56.2395 3.84889 56.2395 3.38925C56.2395 3.00289 56.3594 2.66649 56.5992 2.38004C56.8457 2.0936 57.1954 1.87378 57.6484 1.72057C58.1014 1.56069 58.6309 1.48075 59.2371 1.48075C59.7967 1.48075 60.3096 1.54071 60.7759 1.66061C61.2422 1.78052 61.6286 1.93373 61.935 2.12025V3.709C61.6352 3.52248 61.2888 3.37593 60.8958 3.26934C60.5028 3.1561 60.0998 3.09948 59.6868 3.09948C59.3937 3.09948 59.1805 3.12612 59.0473 3.17941C58.9141 3.22604 58.8474 3.29932 58.8474 3.39924C58.8474 3.49916 58.8874 3.57577 58.9673 3.62906C59.0473 3.68235 59.1838 3.72898 59.377 3.76895C59.3903 3.76895 59.397 3.77228 59.397 3.77894C59.4037 3.77894 59.4137 3.77894 59.427 3.77894L60.3263 3.9588C60.7593 4.0454 61.1157 4.16197 61.3954 4.30852C61.6752 4.44841 61.8884 4.64159 62.0349 4.88807C62.1881 5.12788 62.2647 5.4343 62.2647 5.80734C62.2647 6.20036 62.1482 6.54676 61.915 6.84652C61.6819 7.14628 61.3355 7.37943 60.8758 7.54597C60.4229 7.70584 59.87 7.78578 59.2172 7.78578Z"
                fill="white"
              />
            </svg>
          </button>
        </CardFooter>
      )}
    </Card>
  );

  const renderWidget = () => (
    <div
      className={`fixed ${
        position === "bottom-right" ? "right-4" : "left-4"
      } bottom-[60px] z-[50] font-sans`}
    >
      <AnimatePresence mode="wait">
        {isOpen ? (
          <motion.div
            key="open"
            initial={{ opacity: 0, scale: 0.3, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.3, y: 50 }}
            transition={{ duration: 0.3 }}
            className="w-full h-full inset-0 md:w-[45vh] md:h-[80vh] md:relative md:inset-auto"
          >
            {renderContent()}
          </motion.div>
        ) : !isOpened ? (
          <motion.div
            key="closed"
            initial={{ opacity: 0, scale: 0.3 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.3 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(true);
              setIsOpened(true);
            }}
            className={`relative rounded-2xl w-32 md:w-52 h-52 md:h-80 overflow-hidden ${
              isVideoLoaded ? "" : "invisible"
            }`}
          >
            <div className="relative w-full h-full cursor-pointer hover:scale-110 transition-all duration-300">
              <video
                preload="auto"
                className="w-full h-full object-cover"
                playsInline
                autoPlay
                muted
                loop
                controls={false}
                onLoadedMetadata={() => setIsVideoLoaded(true)}
              >
                <source src={videos[0]} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="absolute inset-0 flex items-center justify-center">
                <PlayIcon className="w-6 h-6 text-white" />
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsOpened(true);
              }}
              className="absolute top-2 right-2 bg-black/40 backdrop-blur-sm flex items-center justify-center rounded-full p-1"
            >
              <XIcon className="w-3 h-3 text-white" />
            </button>
            <div
              className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black to-transparent rounded-b-2xl pt-8 pointer-events-none"
              dir={isRtl ? "rtl" : "ltr"}
            >
              <p className="text-lg leading-6 md:text-xl font-semibold text-center text-white md:leading-7 mb-1">
                {widgetData.title}
              </p>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="fab"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-[60px] right-4 rounded-full w-14 h-14 shadow-lg hover:shadow-xl transition-shadow duration-300"
          >
            <FAB onClick={() => setIsOpen(true)} imageUrl={logoUrl} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const renderStory = () => (
    <div className="fixed top-0 left-0 right-0 z-[99999] font-sans w-full h-full">
      <AnimatePresence>
        <motion.div
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          exit={{ y: -100 }}
          transition={{ duration: 0.3 }}
        >
          {/* add backfrop blur */}
          <div className="flex justify-center items-center p-2 bg-black/10 backdrop-blur-sm rounded-b-lg w-full h-full md:w-1/2 md:translate-x-1/2">
            {videos.map((_, index) => (
              <div
                key={index}
                className={`w-12 h-12 rounded-full border-2 ${
                  index === currentIndex ? "border-blue-500" : "border-gray-300"
                } mx-1 cursor-pointer overflow-hidden`}
                onClick={() => {
                  setCurrentIndex(index);
                  setIsOpen(true);
                }}
              >
                <video className="w-full h-full object-cover">
                  <source src={videos[index]} type="video/mp4" />
                </video>
              </div>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black bg-opacity-75 z-[99999] flex items-center justify-center"
          >
            <div className="w-full h-full max-w-4xl">{renderContent()}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const renderLightbox = () => (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 bg-black bg-opacity-75 z-[99999] flex items-center justify-center"
      >
        <div className="w-full h-full max-w-4xl max-h-[90vh]">
          {renderContent()}
        </div>
      </motion.div>
    </AnimatePresence>
  );

  switch (variant) {
    case "story":
      return renderStory();
    case "lightbox":
      return renderLightbox();
    default:
      return renderWidget();
  }
};
